/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2022
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Tabs, Tab } from 'carbon-components-react';
import { Switch, Route, withRouter } from 'react-router-dom';
import { MemoryRouter as Router } from 'react-router-dom';
import classNames from 'classnames';
import { useApi } from 'contexts/ApiHandler';
import { useConfig } from 'contexts/config';

import Overview from './Overview';
import TestToolGraphQL from './TestToolGraphQL';

const GraphQLDisplay = ({ infoSectionEntries, documentationEntries, apps }) => {
  const { api, apiHandler } = useApi();
  const { options } = useConfig();

  const { t } = useTranslation();

  let isTestable = options.tryIt && apiHandler.isTestable(api);

  let GraphQLLanding = ({ children }) => (
    <section className="graphql-display__section" aria-label="operation-display">
      {children}
    </section>
  );

  let GraphQLOverview = withRouter(({ history }) => (
    <Tabs className="graphql-display__section-tabs" selected={0}>
      <Tab
        id="graphql-display__section-overview-tab"
        className="graphql-display__section-overview-tab"
        onClick={() => history.push('/')}
        label={t('overview')}
        title={t('overview')}
      >
        <section className={classNames('tab-content', 'overview')} aria-label="graphql-overview">
          <div className="graphql-display__section-overview-tab-contents">
            <Overview infoSectionEntries={infoSectionEntries} documentationEntries={documentationEntries} />
          </div>
        </section>
      </Tab>
      {isTestable && <Tab id="TryItTab" onClick={() => history.push('/try-it')} label={t('try')} />}
    </Tabs>
  ));

  let TestGraphQL = withRouter(({ history }) => (
    <Tabs className="graphql-display__section-tabs" selected={1}>
      <Tab
        id="test-graphql-display__section-overview-tab"
        className="graphql-display__section-overview-tab"
        onClick={() => history.push('/')}
        label={t('overview')}
        title={t('overview')}
      />
      {isTestable && (
        <Tab id="testTab" className="testTab" onClick={() => history.push('/try-it')} label={t('try')} title={t('try')}>
          <TestToolGraphQL api={api} options={options} apps={apps} />
        </Tab>
      )}
    </Tabs>
  ));

  const GraphQLOverviewContent = () => (
    <GraphQLLanding>
      <GraphQLOverview />
    </GraphQLLanding>
  );

  const TestGraphQLContent = () => (
    <GraphQLLanding>
      <TestGraphQL />
    </GraphQLLanding>
  );

  return (
    <Router>
      <div className={classNames('apiconnect-explorer', 'graphql-display', 'main')} aria-label="graphqldisplay-main">
        <Switch>
          <Route exact={true} path="/" component={GraphQLOverviewContent} />
          {isTestable && <Route exact={true} path="/try-it" component={TestGraphQLContent} />}
        </Switch>
      </div>
    </Router>
  );
};
// needed for finding in tests
GraphQLDisplay.displayName = 'GraphQLDisplay';
GraphQLDisplay.propTypes = {
  infoSectionEntries: PropTypes.array,
  documentationEntries: PropTypes.array,
  apps: PropTypes.array
};

export default GraphQLDisplay;
